import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams, useResolvedPath } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { getTradingAccountIcon } from '@pages/Trade/TradingAccountInfo/utils/getLogoForAccount'
import { DepositStartLocationState, DepositTarget } from '@pages/Traders-Room/Deposit/hooks/types'

import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Heading2 } from '@ui/Typography'
import { Chip } from '@ui/components/Chip'
import { Divider } from '@ui/components/Divider'
import { PopoverInput } from '@ui/components/Input'
import { PopoverOption } from '@ui/components/Popover/PopoverInput/PopoverInput'
import { Tab, Tabs } from '@ui/components/Tabs'
import { Tooltip } from '@ui/components/Tooltip'

import { useGAEvents } from '@utils/callGAEvents'
import { useWindowResize } from '@utils/domUtils'
import { WalletRestrictions, isWalletRestrictionsAllowed } from '@utils/wallet.utils'

import type { TradingAccount as TradingAccountModel } from '@model/TradingAccount'
import { WalletTypeEnum } from '@model/WalletDto'
import { WalletPaymentProvider } from '@model/WalletPaymentProvider'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingExit } from '@hooks/useFundingExit'

import { AnimatedSwitch } from '@shared/animations/AnimatedSwitch'
import { Box } from '@shared/components/Box'
import { FundingMethodList } from '@shared/components/Funding/FundingMethodList'
import { Loading } from '@shared/components/Loading/Loading'
import { FundingTemplate } from '@shared/templates/FundingTemplate'

import { DestinationSelectedProps, useDepositTracker } from '@analytics/amplitude/deposit'

import { useWalletCurrencyIcons } from '../shared/hooks/useWalletCurrencyIcons'
import { useDepositMethods } from './hooks/useDepositMethods'
import { useDepositSelections } from './hooks/useDepositSelections'
import { useDepositTarget } from './hooks/useDepositTarget'
import { useDepositTradingAccounts } from './hooks/useDepositTradingAccounts'
import { useDepositWallets } from './hooks/useDepositWallets'

const getTradingAccountLabel = (account: TradingAccountModel): string => {
  const login = account.name ?? ''
  return `${login}`
}

enum DepositTargetIndex {
  Wallet = 0,
  TradingAccount = 1,
}

export const DepositPage: FC<{ dashboardContext?: 'traders-room' | 'payment-agent' }> = ({
  dashboardContext = 'traders-room',
}) => {
  const { t } = useTranslation()
  const {
    target: targetParam,
    id: idParam,
    pspId,
  } = useParams<{ target?: string; id?: string; pspId?: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const locale = useSessionLanguage()
  const isMobile = useWindowResize()
  const { isDefaultCFDProductType } = useProductReadContext()
  const tracker = useDepositTracker()
  const { formatMoney } = useFormatNumber()
  const { handleExit } = useFundingExit()

  useEffect(() => {
    tracker.start({ entry_point: 'direct_link', source_page: 'direct_link' })
  }, [tracker])

  const isCFD = isDefaultCFDProductType() && dashboardContext === 'traders-room'
  const walletType =
    dashboardContext === 'payment-agent'
      ? WalletTypeEnum.PAYMENT_AGENT
      : isCFD
      ? WalletTypeEnum.CFD
      : WalletTypeEnum.ETD

  const { callGAEvents } = useGAEvents()
  const target = useDepositTarget({ isCFD, targetParam, dashboardContext })

  const { wallets, isWalletsLoading } = useDepositWallets(walletType)
  const { tradingAccounts, isTradingAccountsLoading } = useDepositTradingAccounts(isCFD)

  const {
    selectedWalletId,
    setSelectedWalletId,
    selectedTradingAccountId,
    setSelectedTradingAccountId,
    selectedWallet,
    selectedTradingAccount,
    allowedTradingAccounts,
    isSelectedWalletAllowed,
    locationState,
  } = useDepositSelections({
    wallets,
    tradingAccounts,
    target,
    idParam,
  })

  const walletCurrencyIcons = useWalletCurrencyIcons(wallets)

  const walletPath = useResolvedPath('../wallet').pathname
  const tradingAccountPath = useResolvedPath('../trading-account').pathname

  const hasTradingAccounts = allowedTradingAccounts.length > 0
  const showTabs = isCFD && hasTradingAccounts

  const isInitialLoading = isWalletsLoading || (isCFD && isTradingAccountsLoading)

  const tabs: Tab<{ pathname: string; state: DepositStartLocationState }>[] = useMemo(
    () => [
      {
        path: { pathname: tradingAccountPath, state: locationState },
        label: t('Trading Account.Trading Account'),
      },
      { path: { pathname: walletPath, state: locationState }, label: t('Wallet.Wallet') },
    ],
    [t, walletPath, tradingAccountPath, locationState]
  )

  const [tradingAccountSearch, setTradingAccountSearch] = useState('')
  const trackedDestinationKeyRef = useRef<string | null>(null)
  const manualSelectionRef = useRef<{ target: DepositTarget; id: string } | null>(null)

  const tradingAccountOptions = useMemo<PopoverOption<string>[]>(() => {
    const q = tradingAccountSearch.trim().toLowerCase()

    const list =
      q.length === 0
        ? allowedTradingAccounts
        : allowedTradingAccounts.filter((a) => getTradingAccountLabel(a).toLowerCase().includes(q))

    return list.map((a) => {
      const platformId = a.platformType?.id
      const platformLabel = a.platformType?.name ?? a.platformType?.id ?? ''

      const balance = formatMoney(a.platformOverview.balance, a.currency.id)
      const campaignName = a.campaigns[0]?.name

      const balanceLabel = campaignName
        ? `${t('Trading Account.Balance')}: ${balance}\n${t(
            'Trading Account.Campaign'
          )}: ${campaignName}`
        : `${t('Trading Account.Balance')}: ${balance}`

      return {
        id: a.id,
        name: getTradingAccountLabel(a),
        startIcon: platformId ? getTradingAccountIcon(platformId) : undefined,
        description: balanceLabel,
        selectionVariant: 'radio',
        trailText: String(platformLabel),
      }
    })
  }, [allowedTradingAccounts, formatMoney, t, tradingAccountSearch])

  const tabIndex =
    target === 'trading-account' ? DepositTargetIndex.TradingAccount : DepositTargetIndex.Wallet
  const prevIndexRef = useRef(tabIndex)

  useEffect(() => {
    callGAEvents({
      deposit_target: target || 'unknown',
    }).Deposit.Funnel.started()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    prevIndexRef.current = tabIndex
  }, [tabIndex])

  useEffect(() => {
    if (isInitialLoading) return
    if (target !== 'trading-account') return
    if (hasTradingAccounts) return

    navigate(`${walletPath}${location.search}`, { replace: true, state: locationState })
  }, [
    isInitialLoading,
    target,
    hasTradingAccounts,
    navigate,
    walletPath,
    location.search,
    locationState,
  ])

  const { methods, isMethodsLoading } = useDepositMethods({
    target,
    locale,
    selectedWalletId: selectedWallet?.id,
    selectedTradingAccountWalletId: selectedTradingAccount?.wallet?.id,
  })
  const showMethodListLoader =
    isMethodsLoading || (target === 'trading-account' && !hasTradingAccounts)

  useEffect(() => {
    if (isMethodsLoading || methods.length === 0) return

    const selectedId = target === 'wallet' ? selectedWalletId : selectedTradingAccountId
    if (!selectedId) return

    if (pspId) {
      const provider = methods.find((m) => m.method.id === pspId)
      navigate(
        `/dashboard/${dashboardContext}/deposit/${target}/${selectedId}/${pspId}/amount${location.search}`,
        {
          state: { ...locationState, walletPaymentProvider: provider },
          replace: true,
        }
      )
    }
  }, [
    isMethodsLoading,
    methods,
    target,
    selectedWalletId,
    selectedTradingAccountId,
    locationState,
    pspId,
    navigate,
    dashboardContext,
    location.search,
  ])

  const currentDestination = useMemo(() => {
    if (target === 'wallet' && selectedWallet) {
      return {
        target: 'wallet' as const,
        id: selectedWallet.id,
        currency: selectedWallet.currency.id,
      }
    }

    if (target === 'trading-account' && selectedTradingAccount) {
      return {
        target: 'trading-account' as const,
        id: selectedTradingAccount.id,
        currency: selectedTradingAccount.currency.id,
      }
    }

    return null
  }, [selectedTradingAccount, selectedWallet, target])

  useEffect(() => {
    if (isInitialLoading || !currentDestination) return

    const destinationKey = `${currentDestination.target}:${currentDestination.id}`
    if (trackedDestinationKeyRef.current === destinationKey) return

    const selectionType: DestinationSelectedProps['selection_type'] =
      manualSelectionRef.current?.target === currentDestination.target &&
      manualSelectionRef.current?.id === currentDestination.id
        ? 'manual'
        : 'preset'

    tracker.destinationSelected({
      destination: currentDestination.target,
      destination_id: currentDestination.id,
      currency: currentDestination.currency,
      selection_type: selectionType,
    })

    trackedDestinationKeyRef.current = destinationKey
    manualSelectionRef.current = null
  }, [currentDestination, isInitialLoading, tracker])

  return (
    <FundingTemplate
      isLoading={isInitialLoading}
      onBack={handleExit}
      maxWidth={580}
      header={
        <Box flex direction='column' gap='x24' alignSelf='stretch'>
          <Heading2 align='center' medium>
            {t('Add funds')}
          </Heading2>

          {!locationState.isDestinationPreselected && (
            <Box flex direction='column' gap='x16'>
              {showTabs && (
                <Box>
                  <Tabs initialIndex={tabIndex} center tabs={tabs} replace />
                  <Divider.Horizontal />
                </Box>
              )}

              <AnimatedSwitch activeKey={target} activeIndex={target === 'wallet' ? 0 : 1}>
                {target === 'wallet' && (
                  <Box flex py='x4' gap='x12' justify='center' wrap>
                    {wallets.map((w) => {
                      const enabled = isWalletRestrictionsAllowed(w, WalletRestrictions.DEPOSIT)

                      return (
                        <Tooltip
                          key={w.id}
                          content={t('Deposits for this wallet are unavailable')}
                          disabled={enabled}
                        >
                          <Chip
                            size='sm'
                            selected={enabled && w.id === selectedWalletId}
                            disabled={!enabled && !isMobile}
                            aria-disabled={!enabled}
                            iconLeft={walletCurrencyIcons[w.currency.id]}
                            onClick={() => {
                              if (!enabled) return
                              manualSelectionRef.current = { target: 'wallet', id: w.id }
                              setSelectedWalletId(w.id)
                            }}
                          >
                            {`${w.currency.id} ${t('Wallet.Wallet')}`}
                          </Chip>
                        </Tooltip>
                      )
                    })}
                  </Box>
                )}

                {target === 'trading-account' && !!allowedTradingAccounts.length && (
                  <Box fullWidth justifySelf='center' maxWidth={476}>
                    <PopoverInput
                      showLabel
                      value={selectedTradingAccount?.id}
                      label={t('Trading Account.Trading Account')}
                      placeholder={`${t('Select')} ${t('Trading Account.Trading Account')}`}
                      required
                      startIcon={
                        selectedTradingAccount
                          ? getTradingAccountIcon(selectedTradingAccount.platformType.id)
                          : undefined
                      }
                      size='lg'
                      options={tradingAccountOptions}
                      hasCheckMark={false}
                      onChange={(option) => {
                        manualSelectionRef.current = {
                          target: 'trading-account',
                          id: option.id,
                        }
                        setSelectedTradingAccountId(option.id)
                        setTradingAccountSearch('')
                      }}
                    />
                  </Box>
                )}
              </AnimatedSwitch>
            </Box>
          )}
        </Box>
      }
    >
      <Loading isLoading={showMethodListLoader} showLoadingIcon>
        <FundingMethodList
          methods={methods}
          disabled={target === 'wallet' ? !isSelectedWalletAllowed : !selectedTradingAccount}
          onSelect={(provider: WalletPaymentProvider) => {
            if (target === 'wallet' && (!selectedWallet || !isSelectedWalletAllowed)) return
            if (target === 'trading-account' && !selectedTradingAccount) return

            tracker.methodSelected({
              method_id: provider.method.id,
              method_type: provider.method.name,
              provider: provider.name,
              is_last_used: Boolean(provider.lastDeposit),
            })
            tracker.stepChanged({
              from: 'destination_method',
              to: 'amount_terms',
              direction: 'forward',
            })

            const targetId = target === 'wallet' ? selectedWallet?.id : selectedTradingAccount?.id
            if (!targetId) return

            navigate(
              `/dashboard/${dashboardContext}/deposit/${target}/${targetId}/${provider.method.id}/amount${location.search}`,
              {
                state: {
                  ...locationState,
                  walletPaymentProvider: provider,
                  selectedWalletId,
                  tradingAccountId: selectedTradingAccountId,
                },
              }
            )
          }}
        />
      </Loading>
    </FundingTemplate>
  )
}
